import React from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Settings, Check, UserCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@shared/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@shared/ui/avatar';
import { Button } from '@shared/ui/button';
import { useAuth, Role } from '@shared/auth/AuthContext';

const AVAILABLE_ROLES: { id: Role; label: string; desc: string }[] = [
  { id: 'owner', label: 'Owner / Director', desc: 'Full institutional control' },
  { id: 'admin', label: 'Branch Admin', desc: 'Operations & scheduling' },
  { id: 'teacher', label: 'Teacher / Faculty', desc: 'Classes, tests & grading' },
  { id: 'counselor', label: 'Admissions Counselor', desc: 'Leads & CRM pipeline' },
  { id: 'accountant', label: 'Accountant', desc: 'Fee collections & ledger' },
  { id: 'student', label: 'Enrolled Student', desc: 'Live classes, assignments' },
  { id: 'parent', label: 'Guardian / Parent', desc: 'Multi-child fee & report portal' },
];

export const UserMenu: React.FC = () => {
  const { user, role, setRole, logout } = useAuth();

  if (!user) {
    return (
      <Button size="sm" asChild variant="secondary" className="h-8 text-xs">
        <Link to="/login">Sign In</Link>
      </Button>
    );
  }

  const initials = user.email
    ? user.email.substring(0, 2).toUpperCase()
    : 'HC';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="rounded-full ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 flex items-center gap-1.5 p-0.5 text-left"
          aria-label="User profile menu"
        >
          <Avatar className="h-7 w-7 text-xs bg-primary text-primary-foreground font-semibold cursor-pointer">
            <AvatarFallback className="bg-primary/20 text-primary font-bold">
              {initials}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60" sideOffset={8}>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-xs font-semibold leading-none text-foreground truncate">
              {user.name || user.email}
            </p>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-muted-foreground uppercase font-mono tracking-wider">
                {role}
              </span>
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="flex items-center gap-2 cursor-pointer text-xs">
            <UserCircle className="h-3.5 w-3.5 text-muted-foreground" />
            <span>Switch Role Shell</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-56 p-1">
            <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1">
              Select Active Role
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-1" />
            {AVAILABLE_ROLES.map((r) => {
              const isActive = role === r.id;
              return (
                <DropdownMenuItem
                  key={r.id}
                  onClick={() => setRole(r.id)}
                  className="flex items-center justify-between text-xs cursor-pointer py-1.5"
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-foreground">{r.label}</span>
                    <span className="text-[10px] text-muted-foreground">{r.desc}</span>
                  </div>
                  {isActive && <Check className="h-3.5 w-3.5 text-primary shrink-0 ml-2" />}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuItem asChild>
          <Link to="/platform/branding" className="flex items-center gap-2 cursor-pointer text-xs">
            <Settings className="h-3.5 w-3.5 text-muted-foreground" />
            <span>Platform Settings</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => logout()}
          className="flex items-center gap-2 text-destructive focus:text-destructive cursor-pointer text-xs"
        >
          <LogOut className="h-3.5 w-3.5" />
          <span>Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
